import { useState, useEffect, useCallback } from 'react';

/**
 * 반응형 브레이크포인트 상수
 * 두 컴포넌트(RoomHeader, RoomRightMenu)에서 동일하게 사용
 */
export const BREAKPOINTS = {
    /** 모바일/태블릿과 데스크톱을 구분하는 기준 */
    DESKTOP: 1200,
    /** 사이드바 표시/숨김 기준 */
    SIDEBAR_COLLAPSE: 1024,
    /** 태블릿과 모바일을 구분하는 기준 */
    TABLET: 768,
    /** 모바일 기준 */
    MOBILE: 480,
} as const;

interface WindowSize {
    innerWidth: number;
    innerHeight: number;
}

interface ResponsiveState {
    /** 현재 윈도우 크기 */
    windowSize: WindowSize;
    /** 데스크톱 화면인지 (>= 1200px) */
    isDesktop: boolean;
    /** 모바일/태블릿 화면인지 (< 1200px) */
    isMobileOrTablet: boolean;
    /** 사이드바가 접혀야 하는지 (< 1024px) */
    isSidebarCollapsed: boolean;
    /** 태블릿 화면인지 (768px ~ 1199px) */
    isTablet: boolean;
    /** 모바일 화면인지 (< 768px) */
    isMobile: boolean;
    /** 작은 모바일 화면인지 (< 480px) */
    isSmallMobile: boolean;
}

/**
 * 화면 크기에 따른 반응형 상태를 제공하는 커스텀 훅
 * RoomHeader와 RoomRightMenu에서 동일하게 사용
 */
export function useResponsive(): ResponsiveState {
    const getWindowSize = useCallback((): WindowSize => {
        return {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
        };
    }, []);

    const [windowSize, setWindowSize] = useState<WindowSize>(getWindowSize);

    useEffect(() => {
        function handleResize() {
            setWindowSize(getWindowSize());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [getWindowSize]);

    const isDesktop = windowSize.innerWidth >= BREAKPOINTS.DESKTOP;
    const isMobileOrTablet = windowSize.innerWidth < BREAKPOINTS.DESKTOP;
    const isSidebarCollapsed = windowSize.innerWidth < BREAKPOINTS.SIDEBAR_COLLAPSE;
    const isTablet = windowSize.innerWidth >= BREAKPOINTS.TABLET && windowSize.innerWidth < BREAKPOINTS.DESKTOP;
    const isMobile = windowSize.innerWidth < BREAKPOINTS.TABLET;
    const isSmallMobile = windowSize.innerWidth < BREAKPOINTS.MOBILE;

    return {
        windowSize,
        isDesktop,
        isMobileOrTablet,
        isSidebarCollapsed,
        isTablet,
        isMobile,
        isSmallMobile,
    };
}

/**
 * 오른쪽 메뉴 표시/숨기기 유틸리티 함수
 */
export function showRightMenu() {
    document.getElementById('right-menu')?.classList.add('show-right-menu');
}

export function hideRightMenu() {
    document.getElementById('right-menu')?.classList.remove('show-right-menu');
}
